import { useMemo } from 'react';
import { useQuery } from 'hooks/useCustomQuery';
import getAvailableServiceProfiles from 'API/ServiceProfiles/getAvailableServiceProfiles';
import { PlatformEnums } from 'utils/platforms';
import { isOperatorPlatform } from 'utils/utils';

/**
 * The company's operator platform, shaped as a numbers-filter entry.
 *
 * Operator platforms are branded per company, so the local platform map cannot
 * name them. Their display name lives on the service profile instead. They all
 * share one platformID, so however many profiles come back they collapse to a
 * single filter entry — a specific name only applies when there is exactly one.
 */
const useGetOperatorPlatform = (currentCompany, enabled) => {
    const query = useQuery({
        queryKey: ['numbersOperatorPlatform', currentCompany],
        queryFn: () => getAvailableServiceProfiles(currentCompany),
        refetchOnWindowFocus: false,
        enabled: Boolean(enabled && currentCompany),
    });

    const operatorPlatform = useMemo(() => {
        const profiles = query.data?.filter((profile) =>
            isOperatorPlatform(profile?.serviceDefinition?.platformID),
        );

        if (!profiles?.length) return undefined;

        return {
            id: PlatformEnums.OPERATOR_PLATFORM,
            name:
                (profiles.length === 1 && profiles[0]?.customDisplayName) ||
                'Operator Platform',
        };
    }, [query.data]);

    // isInitialLoading, not isLoading: React Query reports a disabled query as
    // loading, which would stall the filter at every other scope.
    return { operatorPlatform, isLoading: query.isInitialLoading };
};

export default useGetOperatorPlatform;
